import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/lib/api/client'
import EventPayIcon from '@/assets/EventPay-Icon.svg'
import { Check } from 'lucide-react'

export const Route = createFileRoute('/account/activation/$email/$code')({
  component: AccountActivationPage,
})

function AccountActivationPage() {
  const { email, code } = Route.useParams()
  const navigate = useNavigate()
  
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!firstName.trim() || !lastName.trim() || !password.trim()) {
      setError('Please provide all required fields.')
      return
    }

    setIsLoading(true)

    try {
      const result = await apiClient.verifyPasswordResetCode(
        decodeURIComponent(email),
        code,
        password,
        firstName,
        lastName
      )

      if (result.success) {
        // Redirect to login with success message
        navigate({ 
          to: '/auth/login',
          search: { redirect: '/events', activated: 'true' }
        })
      } else {
        setError(result.error || 'Invalid code or failed to activate account. Please try again.')
      }
    } catch (err) {
      setError('An error occurred during account activation. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const decodedEmail = decodeURIComponent(email)

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sky background with clouds effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-300 via-sky-200 to-sky-100" />
      
      {/* Decorative circles/arcs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full border border-white/30" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-white/20" />
      </div>
      
      {/* Cloud-like shapes */}
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-white/80 to-transparent" />
      <div className="absolute bottom-20 left-10 w-64 h-32 bg-white/50 rounded-full blur-3xl" />
      <div className="absolute bottom-32 right-20 w-80 h-40 bg-white/40 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-1/3 w-72 h-36 bg-white/60 rounded-full blur-3xl" />
      
      {/* Activation card */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div 
          className="w-full max-w-md animate-fade-in-up"
          style={{
            animation: 'fadeInUp 0.6s ease-out forwards',
          }}
        >
          <div className="bg-gradient-to-b from-white/95 to-sky-50/90 backdrop-blur-xl rounded-3xl shadow-xl shadow-sky-200/50 border border-white/60 p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={EventPayIcon} 
                alt="EventPay" 
                className="w-16 h-16 rounded-2xl shadow-lg shadow-blue-500/30"
              />
            </div>
            
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                Activate Your Account
              </h1>
              <p className="text-neutral-500 text-sm">
                Please enter your new password
              </p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50/80 rounded-xl border border-red-100">
                  {error}
                </div>
              )}
              
              {/* Email and Code Row */}
              <div className="flex gap-3">
                {/* Email field - 70% */}
                <div className="flex-[7]">
                  <label className="block text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1.5">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={decodedEmail}
                    readOnly
                    className="h-11 rounded-xl bg-neutral-100/80 border-neutral-200/80 text-neutral-600 cursor-not-allowed"
                  />
                  <div className="flex items-center mt-1.5">
                    <Check className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-xs text-green-600">{decodedEmail}</span>
                  </div>
                </div>
                
                {/* Code field - 30% */}
                <div className="flex-[3]">
                  <label className="block text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1.5">
                    Code
                  </label>
                  <Input
                    type="text"
                    value={code}
                    readOnly
                    className="h-11 rounded-xl bg-neutral-100/80 border-neutral-200/80 text-neutral-600 cursor-not-allowed"
                  />
                </div>
              </div>
              
              {/* First Name and Last Name Row */}
              <div className="flex gap-3">
                {/* First Name */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1.5">
                    First Name<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-11 rounded-xl bg-white/80 border-neutral-200/80 focus:bg-white transition-colors"
                  />
                </div>
                
                {/* Last Name */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1.5">
                    Last Name<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Smith"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="h-11 rounded-xl bg-white/80 border-neutral-200/80 focus:bg-white transition-colors"
                  />
                </div>
              </div>
              
              {/* Password Row */}
              <div>
                <label className="block text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1.5">
                  Password<span className="text-red-500 ml-0.5">*</span>
                </label>
                <Input
                  type="password"
                  placeholder="**********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 rounded-xl bg-white/80 border-neutral-200/80 focus:bg-white transition-colors"
                />
              </div>
              
              {/* Submit button */}
              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-base shadow-lg shadow-blue-500/20 transition-all hover:shadow-xl hover:shadow-blue-500/30 mt-2" 
                isLoading={isLoading}
              >
                Submit
              </Button>
            </form>
            
            {/* Sign In link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-500">
                Get back to{' '}
                <Link
                  to="/auth/login"
                  search={{ redirect: '/events', activated: undefined }}
                  className="text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                >
                  Sign In
                </Link>
                ?
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}
